import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { motion } from "framer-motion";
import {
  Mail,
  Send,
  Building,
  Globe,
  Users,
  Briefcase,
  Target,
  DollarSign,
  Smartphone,
} from "lucide-react";
import SEO from "../../components/Common/SEO";
import { generateCanonicalUrl } from "../../utils/seoUtils";
import { triggerToast } from "../../utils/toast";
import { submitFeedback } from "../../services/feedbackApi";

const Feedback = () => {
  const [submitting, setSubmitting] = useState(false);

  // Application theme colors
  const themeAccent = "#ff2f6d";
  const cardBg = "#21222d";
  const pageBg = "#171821";
  const borderColor = "#353748";
  const inputBg = "#171821";
  const subTextColor = "#a1acb8";
  const [formData, setFormData] = useState({
    email: "",
    companyName: "",
    companyWebsite: "",
    companySize: "",
    businessCategory: "",
    keywordsNeeded: "",
    dailyBudget: "",
    appsToTrack: [],
    otherBusinessCategory: "",
    otherAppsToTrack: "",
  });

  const companySizes = [
    { value: "startup", label: "Startup (1-10 employees)" },
    { value: "small", label: "Small Business (11-50 employees)" },
    { value: "medium", label: "Medium Business (51-200 employees)" },
    { value: "large", label: "Large Enterprise (200+ employees)" },
  ];

  const businessCategories = [
    { value: "ecommerce", label: "E-commerce" },
    { value: "saas", label: "SaaS" },
    { value: "marketing", label: "Marketing" },
    { value: "education", label: "Education" },
    { value: "gaming", label: "Gaming & VR" },
    { value: "finance", label: "Finance & Crypto" },
    { value: "other", label: "Other" },
  ];

  const appOptions = [
    { value: "meta-quest", label: "Meta Quest Store" },
    { value: "steam", label: "Steam" },
    { value: "apple-vision", label: "Apple Vision Pro" },
    { value: "custom-web", label: "Custom Web Apps" },
    { value: "other", label: "Other" },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        appsToTrack: checked
          ? [...prev.appsToTrack, value]
          : prev.appsToTrack.filter((app) => app !== value),
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Basic validation
    if (!formData.email || !formData.companyName || !formData.companySize || 
        !formData.businessCategory || !formData.keywordsNeeded || !formData.dailyBudget) {
      triggerToast("Please fill in all required fields", "error");
      setSubmitting(false);
      return;
    }

    if (formData.businessCategory === "other" && !formData.otherBusinessCategory) {
      triggerToast("Please specify your business category", "error");
      setSubmitting(false);
      return;
    }

    if (formData.appsToTrack.includes("other") && !formData.otherAppsToTrack) {
      triggerToast("Please specify the other apps you want to track", "error");
      setSubmitting(false);
      return;
    }

    try {
      // Prepare the data for API submission
      const feedbackData = {
        email: formData.email,
        company_name: formData.companyName,
        company_website: formData.companyWebsite,
        company_size: formData.companySize,
        business_category: formData.businessCategory === "other" ? formData.otherBusinessCategory : formData.businessCategory,
        keywords_needed: formData.keywordsNeeded,
        daily_budget: formData.dailyBudget,
        apps_to_track: formData.appsToTrack.includes("other") 
          ? [...formData.appsToTrack.filter(app => app !== "other"), formData.otherAppsToTrack]
          : formData.appsToTrack,
      };

      await submitFeedback(feedbackData);
      
      triggerToast("Feedback submitted successfully!", "success");
      
      // Reset form
      setFormData({
        email: "",
        companyName: "",
        companyWebsite: "",
        companySize: "",
        businessCategory: "",
        keywordsNeeded: "",
        dailyBudget: "",
        appsToTrack: [],
        otherBusinessCategory: "",
        otherAppsToTrack: "",
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         "Failed to submit feedback. Please try again.";
      triggerToast(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <SEO
        title="Feedback | KeyQuest VR Analytics Platform"
        description="Share your feedback and help us improve our services"
        canonical={generateCanonicalUrl("/feedback")}
      />
      <div className="page-content py-8 py-md-12" style={{ backgroundColor: pageBg, minHeight: "80vh", fontFamily: "Rubik, sans-serif" }}>
        <Container className="px-3 px-md-4">
          <Row className="justify-content-center">
            <Col lg={8} md={10}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}>
                  <CardBody className="p-4 p-md-5">
                    <div className="text-center mb-5">
                      <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                      >
                        <span className="px-3 py-1 rounded-pill mb-3 d-inline-block" style={{ backgroundColor: "rgba(255, 47, 109, 0.1)", color: themeAccent, fontSize: "12px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                          Feedback
                        </span>
                        <h1 className="text-white fw-bold mb-3" style={{ fontSize: "clamp(1.75rem, 4vw, 2.5rem)", letterSpacing: "-0.02em" }}>
                          Give Us Your Feedback
                        </h1>
                        <p style={{ color: subTextColor, fontSize: "15px", lineHeight: "1.7" }}>
                          Help us understand your needs better and improve our services
                        </p>
                      </motion.div>
                    </div>

                <Form onSubmit={handleSubmit}>
                  {/* Email Address */}
                    <FormGroup className="mb-4">
                      <Label for="email" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Mail className="me-2" size={16} color={themeAccent} />
                        Email Address *
                      </Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        style={{
                          backgroundColor: inputBg,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          color: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                        required
                      />
                    </FormGroup>

                  {/* Company Name */}
                    <FormGroup className="mb-4">
                      <Label for="companyName" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Building className="me-2" size={16} color={themeAccent} />
                        Company Name *
                      </Label>
                      <Input
                        type="text"
                        name="companyName"
                        id="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Your company name"
                        style={{
                          backgroundColor: inputBg,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          color: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                        required
                      />
                    </FormGroup>

                  {/* Company Website URL */}
                    <FormGroup className="mb-4">
                      <Label for="companyWebsite" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Globe className="me-2" size={16} color={themeAccent} />
                        Company Website URL
                      </Label>
                      <Input
                        type="url"
                        name="companyWebsite"
                        id="companyWebsite"
                        value={formData.companyWebsite}
                        onChange={handleInputChange}
                        placeholder="https://yourcompany.com"
                        style={{
                          backgroundColor: inputBg,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          color: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                      />
                    </FormGroup>

                  {/* Measure of Company Size */}
                    <FormGroup className="mb-4">
                      <Label style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Users className="me-2" size={16} color={themeAccent} />
                        Measure of Company Size *
                      </Label>
                      <div style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: `1px solid ${borderColor}`, borderRadius: "8px", padding: "1rem" }}>
                        {companySizes.map((size) => (
                          <div key={size.value} className="form-check mb-2">
                            <Input
                              type="radio"
                              name="companySize"
                              id={`size-${size.value}`}
                              value={size.value}
                              checked={formData.companySize === size.value}
                              onChange={handleInputChange}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(255, 255, 255, 0.3)", borderRadius: "4px" }}
                              required
                            />
                            <Label
                              for={`size-${size.value}`}
                              className="form-check-label ms-2"
                              style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "14px" }}
                            >
                              {size.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>

                  {/* Business Category */}
                    <FormGroup className="mb-4">
                      <Label style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Briefcase className="me-2" size={16} color={themeAccent} />
                        Business Category *
                      </Label>
                      <div style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: `1px solid ${borderColor}`, borderRadius: "8px", padding: "1rem" }}>
                        {businessCategories.map((category) => (
                          <div key={category.value} className="form-check mb-2">
                            <Input
                              type="radio"
                              name="businessCategory"
                              id={`category-${category.value}`}
                              value={category.value}
                              checked={formData.businessCategory === category.value}
                              onChange={handleInputChange}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(255, 255, 255, 0.3)", borderRadius: "4px" }}
                              required
                            />
                            <Label
                              for={`category-${category.value}`}
                              className="form-check-label ms-2"
                              style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "14px" }}
                            >
                              {category.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {formData.businessCategory === "other" && (
                        <Input
                          type="text"
                          name="otherBusinessCategory"
                          value={formData.otherBusinessCategory}
                          onChange={handleInputChange}
                          placeholder="Please specify"
                          style={{
                            backgroundColor: inputBg,
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            color: "white",
                            borderRadius: "8px",
                            fontSize: "14px",
                            padding: "12px",
                            marginTop: "0.75rem"
                          }}
                          required
                        />
                      )}
                    </FormGroup>

                  {/* How Many Keywords Do You Need to Track */}
                    <FormGroup className="mb-4">
                      <Label for="keywordsNeeded" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Target className="me-2" size={16} color={themeAccent} />
                        How Many Keywords Do You Need to Track? *
                      </Label>
                      <Input
                        type="text"
                        name="keywordsNeeded"
                        id="keywordsNeeded"
                        value={formData.keywordsNeeded}
                        onChange={handleInputChange}
                        placeholder="Example: 50, 100, 500, etc."
                        style={{
                          backgroundColor: inputBg,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          color: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                        required
                      />
                    </FormGroup>

                  {/* Daily Budget for Tracking */}
                    <FormGroup className="mb-4">
                      <Label for="dailyBudget" style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <DollarSign className="me-2" size={16} color={themeAccent} />
                        Daily Budget for Tracking *
                      </Label>
                      <Input
                        type="text"
                        name="dailyBudget"
                        id="dailyBudget"
                        value={formData.dailyBudget}
                        onChange={handleInputChange}
                        placeholder="Example: $50, $100, $500, etc."
                        style={{
                          backgroundColor: inputBg,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          color: "white",
                          borderRadius: "8px",
                          fontSize: "14px",
                          padding: "12px"
                        }}
                        required
                      />
                    </FormGroup>

                  {/* Apps You Want to Track */}
                    <FormGroup className="mb-4">
                      <Label style={{ color: subTextColor, fontSize: "12px", fontWeight: 500 }} className="mb-1">
                        <Smartphone className="me-2" size={16} color={themeAccent} />
                        Apps You Want to Track *
                      </Label>
                      <div style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", border: `1px solid ${borderColor}`, borderRadius: "8px", padding: "1rem" }}>
                        {appOptions.map((app) => (
                          <div key={app.value} className="form-check mb-2">
                            <Input
                              type="checkbox"
                              name="appsToTrack"
                              id={`app-${app.value}`}
                              value={app.value}
                              checked={formData.appsToTrack.includes(app.value)}
                              onChange={handleInputChange}
                              style={{ backgroundColor: "rgba(255, 255, 255, 0.1)", border: "1px solid rgba(255, 255, 255, 0.3)", borderRadius: "4px" }}
                            />
                            <Label
                              for={`app-${app.value}`}
                              className="form-check-label ms-2"
                              style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "14px" }}
                            >
                              {app.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                      {formData.appsToTrack.includes("other") && (
                        <Input
                          type="text"
                          name="otherAppsToTrack"
                          value={formData.otherAppsToTrack}
                          onChange={handleInputChange}
                          placeholder="Please specify other apps"
                          style={{
                            backgroundColor: inputBg,
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            color: "white",
                            borderRadius: "8px",
                            fontSize: "14px",
                            padding: "12px",
                            marginTop: "0.75rem"
                          }}
                          required
                        />
                      )}
                    </FormGroup>

                  {/* Submit Button */}
                    <div className="text-center mt-5">
                      <Button
                        type="submit"
                        disabled={submitting}
                        style={{
                          backgroundColor: themeAccent,
                          border: "none",
                          borderRadius: "10px",
                          padding: "12px 30px",
                          fontWeight: 600,
                          fontSize: "15px",
                          transition: "all 0.3s ease",
                        }}
                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            Submit Feedback
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Feedback;
